import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { prefetchRecommendations } from '@/hooks/useRecommendationPrefetch';
import { prefetchOwnProfile } from '@/hooks/useProfilePrefetch';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: { username?: string; display_name?: string; image?: string; bio?: string }) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Create or update profile when user signs up or signs in
        if (session?.user && event === 'SIGNED_IN') {
          setTimeout(() => {
            ensureProfile(session.user);
            // Prefetch recommendations in background after sign-in
            prefetchRecommendations();
            // Prefetch user's own profile data for faster navigation
            prefetchUserProfileData(session.user.id);
          }, 0);
        }
      }
    );

    // Check for existing session and handle "Remember me" logic
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        const rememberMe = localStorage.getItem('dino_remember_me');
        const sessionActive = sessionStorage.getItem('dino_session_active');
        
        // If user chose not to be remembered and this is a new browser session, sign out
        if (rememberMe === 'false' && !sessionActive) {
          supabase.auth.signOut();
          setSession(null);
          setUser(null);
        } else {
          // Mark session as active for this browser session
          sessionStorage.setItem('dino_session_active', 'true');
          setSession(session);
          setUser(session?.user ?? null);
        }
      } else {
        setSession(null);
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Prefetch user's profile data after sign-in for faster navigation
  const prefetchUserProfileData = async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', userId)
        .maybeSingle();
      
      if (profile?.username) {
        prefetchOwnProfile(userId, profile.username);
      }
    } catch (error) {
      console.error('[Auth] Profile prefetch error:', error);
    }
  };

  const ensureProfile = async (user: User) => {
    try {
      // Use the new atomic ensure_user_profile function (no email needed)
      const { error } = await supabase.rpc('ensure_user_profile');

      if (error) {
        console.error('Profile creation error:', error);
        toast({
          variant: "destructive",
          title: "Profile Creation Failed",
          description: `${error.code}: ${error.message}`,
        });
      }
    } catch (error: any) {
      console.error('Unexpected profile creation error:', error);
      toast({
        variant: "destructive", 
        title: "Unexpected Error",
        description: error.message || "Failed to create user profile",
      });
    }
  };

  // Helper function to get the correct base URL for email redirects
  const getBaseUrl = () => {
    // Always use dinoreads.com in production
    if (import.meta.env.PROD) {
      return 'https://dinoreads.com';
    }
    return window.location.origin;
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${getBaseUrl()}/onboarding`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: email.split('@')[0],
        }
      }
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error.message,
      });
    } else {
      toast({
        title: "Check your email",
        description: "We've sent you a confirmation link.",
      });
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: error.message,
      });
    }

    return { error };
  };

  const signOut = async () => {
    // Clear remember me preferences on explicit sign out
    localStorage.removeItem('dino_remember_me');
    sessionStorage.removeItem('dino_session_active');
    
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Sign out failed",
        description: error.message,
      });
    }
  };

  const updateProfile = async (updates: { username?: string; display_name?: string; image?: string; bio?: string }) => {
    if (!user) return { error: 'No user logged in' };

    // Update the profiles table
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) {
      console.error('Profile update error:', error);
      toast({
        variant: "destructive", 
        title: "Profile update failed",
        description: `${error.code}: ${error.message}`,
      });
    } else {
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    }

    return { error };
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      updateProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}